// service/VentasPresenciales.js
import instance from "../apis/app";

/**
 * Obtiene talleres para venta presencial.
 * El backend devuelve todos los talleres activos (con `estadoInscripcion`) y solo los
 * horarios con cupo; no lee ningún parámetro (`adminMode` se ignora).
 * @returns {Promise}
 */
const ObtenerTalleresDisponibles = async (params = {}) => {
    try {
        const response = await instance.get('/ventas-presenciales/talleres-disponibles', { params });
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al obtener talleres disponibles";
        throw { message: mensaje, status: error.response?.status };
    }
};

/**
 * Inicia una nueva venta presencial
 * @param {Object} data - { tallerId, horarioIds } (horarioIds es array)
 * @returns {Promise}
 */
const IniciarVenta = async (data) => {
    try {
        const response = await instance.post('/ventas-presenciales/iniciar', data);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "No se pudo iniciar la venta";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Busca o registra un usuario por RUT
 * @param {String} ventaId 
 * @param {Object} data - { rut, datosUsuario }
 * @returns {Promise}
 */
const BuscarORegistrarUsuario = async (ventaId, data) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/buscar-usuario`, data);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al buscar usuario";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Selecciona el beneficiario de la inscripción
 * @param {String} ventaId 
 * @param {Object} data - { userId, familiarId }
 * @returns {Promise}
 */
const SeleccionarBeneficiario = async (ventaId, data) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/seleccionar-beneficiario`, data);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al seleccionar beneficiario";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Calcula el precio según el plan seleccionado
 * @param {String} ventaId 
 * @param {String} planNombre - 'mensual' | 'trimestral' | 'semestral' | 'anual' | 'gratuito'
 * @returns {Promise}
 */
const CalcularPrecio = async (ventaId, planNombre, codigoManual = null) => {
    try {
        // codigoManual debe viajar al backend: es el backend quien congela el
        // precio que se cobra. Si no se envía, el descuento del código se
        // muestra en pantalla pero NO se aplica al cobro.
        const response = await instance.post(`/ventas-presenciales/${ventaId}/calcular-precio`, {
            planNombre: planNombre,
            codigoManual: codigoManual || null
        });
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al calcular precio";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Confirma una inscripción gratuita (sin pago)
 * @param {String} ventaId 
 * @returns {Promise}
 */
const ConfirmarInscripcionGratuita = async (ventaId) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/confirmar-gratuita`);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "No se pudo confirmar la inscripción";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Procesa el pago por máquina POS
 * @param {String} ventaId 
 * @param {Object} data - { pagoExitoso, detallesPOS: { numeroVoucher, codigoAutorizacion, ultimosDigitosTarjeta } }
 * @returns {Promise}
 */
const ProcesarPagoPOS = async (ventaId, data) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/pago-pos`, data);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al procesar pago POS";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Inicia el pago por Webpay QR
 * @param {String} ventaId 
 * @returns {Promise}
 */
const IniciarPagoWebpay = async (ventaId) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/pago-webpay/iniciar`);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al iniciar pago Webpay";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Confirma el pago de Webpay
 * @param {String} ventaId 
 * @param {Object} data - { token }
 * @returns {Promise}
 */
const ConfirmarPagoWebpay = async (ventaId, data) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/pago-webpay/confirmar`, data);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al confirmar pago Webpay";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Anula una venta completada (admin). Cancela inscripciones y libera cupos.
 * @param {String} ventaId  - ID de la venta o orden
 * @param {String} tipoVenta - 'presencial' | 'online'
 * @param {String} motivo
 */
const AnularVenta = async (ventaId, tipoVenta, motivo) => {
    try {
        const response = await instance.post(`/admin/ventas/${ventaId}/anular`, { tipoVenta, motivo });
        return response.data;
    } catch (error) {
        const mensaje = error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Error al anular la venta';
        throw { message: mensaje, status: error.response?.status };
    }
};

/**
 * Cancela una venta en proceso
 * @param {String} ventaId
 * @param {Object} data - { motivo }
 * @returns {Promise}
 */
const CancelarVenta = async (ventaId, data) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/cancelar`, data);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al cancelar venta";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Lista todas las ventas con filtros opcionales
 * @param {Object} filtros - { vendedor, estado, fechaDesde, fechaHasta, busqueda, page, limit }
 * @returns {Promise}
 */
const ListarVentas = async (filtros = {}) => {
    try {
        const params = new URLSearchParams();
        
        if (filtros.estado) params.append('estado', filtros.estado);
        if (filtros.fechaDesde) params.append('fechaDesde', filtros.fechaDesde);
        if (filtros.fechaHasta) params.append('fechaHasta', filtros.fechaHasta);
        if (filtros.busqueda) params.append('busqueda', filtros.busqueda);
        if (filtros.vendedor) params.append('vendedor', filtros.vendedor);
        
        params.append('page', filtros.page || 1);
        params.append('limit', filtros.limit || 20);

        const url = `/ventas-presenciales${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await instance.get(url);
        
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al listar ventas";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Obtiene el detalle de una venta específica
 * @param {String} ventaId 
 * @returns {Promise}
 */
const ObtenerVenta = async (ventaId) => {
    try {
        const response = await instance.get(`/ventas-presenciales/${ventaId}`);
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al obtener venta";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Obtiene estadísticas de ventas
 * @param {Object} params - { fechaDesde, fechaHasta }
 * @returns {Promise}
 */
const ObtenerEstadisticas = async (params = {}) => {
    try {
        const response = await instance.get('/ventas-presenciales/estadisticas', { params });
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al obtener estadísticas";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

/**
 * Valida y aplica un código de descuento manual (bomberos, doctores, etc.)
 * @param {String} ventaId
 * @param {String} codigo
 * @returns {Promise}
 */
const AplicarCodigoDescuento = async (ventaId, codigo) => {
    try {
        const response = await instance.post(`/ventas-presenciales/${ventaId}/aplicar-codigo`, { codigo });
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al aplicar código de descuento";
        throw { message: mensaje, status: error.response?.status };
    }
};

/**
 * Limpia reservas expiradas manualmente (solo admin)
 * @returns {Promise}
 */
const LimpiarReservasExpiradas = async () => {
    try {
        const response = await instance.post('/ventas-presenciales/limpiar-expiradas');
        return response;
    } catch (error) {
        const mensaje = error.response?.data?.error ||
            error.response?.data?.message ||
            error.message ||
            "Error al limpiar reservas";

        throw {
            message: mensaje,
            status: error.response?.status
        };
    }
};

export default {
    ObtenerTalleresDisponibles,
    AplicarCodigoDescuento,
    IniciarVenta,
    BuscarORegistrarUsuario,
    SeleccionarBeneficiario,
    CalcularPrecio,
    ConfirmarInscripcionGratuita,
    ProcesarPagoPOS,
    IniciarPagoWebpay,
    ConfirmarPagoWebpay,
    AnularVenta,
    CancelarVenta,
    ListarVentas,
    ObtenerVenta,
    ObtenerEstadisticas,
    LimpiarReservasExpiradas
};
